/**
 * Fume S3 — payload encryption + fragmentation.
 *
 * Pipeline (encrypt side):
 *   1. Pad the plaintext so the *inner ciphertext* is an exact multiple of
 *      FRAGMENT_INNER_SIZE (§5).
 *   2. Inner AEAD encrypt the padded payload under the session content key
 *      `K_c` with `innerAD = "fume.content.v1" || sessionId || T` (§6).
 *   3. Split the inner ciphertext into FRAGMENT_INNER_SIZE-byte chunks; each
 *      chunk is individually AEAD-encrypted (outer) under a per-direction
 *      fragment subkey with a fresh random 24-byte nonce and a per-fragment AD
 *      that binds {kind, index, totalCount, direction, sessionId, T} (§7).
 *   4. Optionally emit indistinguishable chaff fragments up to a fixed total
 *      count (§8, §9).
 *
 * Reassembly authenticates every fragment, drops authenticated chaff, checks
 * that the real fragments form a contiguous 0..realCount-1 index set with no
 * gaps or duplicates, concatenates them, inner-decrypts, and strips padding.
 * Any authentication, ordering, binding, or padding failure is a hard reject
 * (invariant I3 / gate G5).
 *
 * Reviewed crypto only: this module reuses the S0 XChaCha20-Poly1305 AEAD and
 * HKDF-SHA256. It defines no ciphers, MACs, or KDFs of its own.
 *
 * Note on the inner nonce: the padding scheme (§5) fixes `target = 1008` so that
 * `innerCiphertext = padded || tag` is *exactly* a multiple of
 * FRAGMENT_INNER_SIZE (§6, `|innerCiphertext| ≡ 0 (mod 1024)`). That leaves no
 * room on the wire for an inner nonce, so the inner AEAD uses a fixed all-zero
 * 24-byte nonce. This is safe because `K_c` is a single-use per-session content
 * key derived by `deriveKeys` only after PAKE + confirmation + SAS; it encrypts
 * one content stream per session. The inner AD binds `sessionId || T`. See
 * BUILD_REPORT.md for the adjudication.
 */
import { xchacha20poly1305Encrypt, xchacha20poly1305Decrypt } from './aead.js';
import { hkdfSha256 } from './hkdf.js';
import { sodium } from './backend.js';
import { FumeCryptoError } from './errors.js';
import { concatBytes, isAllZero, requireLength, uint64be } from './internal.js';
// ── Constants (§3) ─────────────────────────────────────────────────────────
/** Bytes of inner (outer-plaintext) ciphertext carried per fragment. */
export const FRAGMENT_INNER_SIZE = 1024;
/** XChaCha20-Poly1305 nonce size. */
export const NONCE_SIZE = 24;
/** Poly1305 tag size. */
export const TAG_SIZE = 16;
/** Fixed on-wire size of every fragment: nonce || ciphertext || tag. */
export const FRAGMENT_SIZE = FRAGMENT_INNER_SIZE + NONCE_SIZE + TAG_SIZE; // 1064
/** uint64be length suffix appended by the padding scheme. */
export const PADDING_LENGTH_SIZE = 8;
export const SESSION_ID_SIZE = 32;
export const TRANSCRIPT_HASH_SIZE = 32;
export const CONTENT_KEY_SIZE = 32;
export const DIRECTION_SEND = 0x00;
export const DIRECTION_RECV = 0x01;
/** Fragment classification byte used in the per-fragment AD (§7). */
const KIND_REAL = 0x00;
const KIND_CHAFF = 0x01;
/** `target` from §5: the inner-ciphertext residue modulo FRAGMENT_INNER_SIZE. */
const PAD_TARGET = FRAGMENT_INNER_SIZE - TAG_SIZE; // 1008
/** Domain separators (§4, §6, §7). Bytes are frozen here to avoid re-encoding. */
const utf8 = new TextEncoder();
const INFO_REAL = utf8.encode('fume/fragment/real/v1');
const INFO_CHAFF = utf8.encode('fume/fragment/chaff/v1');
const INNER_AD_PREFIX = utf8.encode('fume.content.v1');
const FRAGMENT_AD_PREFIX = utf8.encode('fume.fragment.v1.');
/** Inner AEAD nonce (see module doc): fixed all-zero, never on the wire. */
const INNER_NONCE = new Uint8Array(NONCE_SIZE);
// ── Byte helpers ─────────────────────────────────────────────────────────────
function uint32be(x) {
    if (!Number.isInteger(x) || x < 0 || x > 0xffffffff) {
        throw new FumeCryptoError(`uint32be requires an integer in 0..2^32-1, got ${x}`);
    }
    const out = new Uint8Array(4);
    new DataView(out.buffer).setUint32(0, x, false);
    return out;
}
function readUint64be(b) {
    const view = new DataView(b.buffer, b.byteOffset, b.byteLength);
    const big = view.getBigUint64(0, false);
    if (big > BigInt(Number.MAX_SAFE_INTEGER)) {
        throw new FumeCryptoError('padding length field exceeds the safe integer range');
    }
    return Number(big);
}
function directionByte(direction) {
    if (direction === 'send')
        return DIRECTION_SEND;
    if (direction === 'recv')
        return DIRECTION_RECV;
    throw new FumeCryptoError(`direction must be 'send' or 'recv'`);
}
// ── Key derivation (§4) ──────────────────────────────────────────────────────
/**
 * Derive the two 32-byte fragment AEAD subkeys from the content key `K_c` and
 * transcript hash `T` with HKDF-SHA256(salt = 0^32, IKM = K_c, info = label||T).
 * `K_c` is never used directly for fragment AEAD.
 */
export function deriveFragmentKeys(contentKey, transcriptHash) {
    requireLength(contentKey, CONTENT_KEY_SIZE, 'contentKey');
    requireLength(transcriptHash, TRANSCRIPT_HASH_SIZE, 'transcriptHash');
    const zeroSalt = new Uint8Array(32);
    return {
        K_frag_real: hkdfSha256(contentKey, zeroSalt, concatBytes(INFO_REAL, transcriptHash), 32),
        K_frag_chaff: hkdfSha256(contentKey, zeroSalt, concatBytes(INFO_CHAFF, transcriptHash), 32),
    };
}
// ── Padding (§5) ─────────────────────────────────────────────────────────────
/**
 * Pad `payload` to `payload || zeros || uint64be(L)` so the padded length is
 * congruent to PAD_TARGET (1008) modulo FRAGMENT_INNER_SIZE — i.e. after the
 * inner AEAD tag is appended the inner ciphertext is an exact multiple of
 * FRAGMENT_INNER_SIZE.
 */
export function padPayload(payload) {
    if (!(payload instanceof Uint8Array)) {
        throw new FumeCryptoError('payload must be a Uint8Array');
    }
    const L = payload.length;
    const zerosLen = (PAD_TARGET - ((L + PADDING_LENGTH_SIZE) % FRAGMENT_INNER_SIZE) + FRAGMENT_INNER_SIZE) %
        FRAGMENT_INNER_SIZE;
    const padded = new Uint8Array(L + zerosLen + PADDING_LENGTH_SIZE);
    padded.set(payload, 0);
    // zeros region [L, L+zerosLen) is already zero-filled by allocation.
    padded.set(uint64be(L), L + zerosLen);
    return padded;
}
/**
 * Inverse of {@link padPayload}. Reads the trailing length, verifies the
 * declared length is in range and that every intervening pad byte is zero, then
 * returns the exact original payload. Rejects (throws) on any inconsistency.
 */
export function stripPadding(padded) {
    if (!(padded instanceof Uint8Array) || padded.length < PADDING_LENGTH_SIZE) {
        throw new FumeCryptoError('padded payload is too short to contain a length field');
    }
    const dataEnd = padded.length - PADDING_LENGTH_SIZE;
    const L = readUint64be(padded.subarray(dataEnd));
    if (L > dataEnd) {
        throw new FumeCryptoError('padding length field exceeds the available payload region');
    }
    if (!isAllZero(padded.subarray(L, dataEnd))) {
        throw new FumeCryptoError('padding region is not all zero (tampered padding)');
    }
    return padded.slice(0, L);
}
// ── Associated data (§6, §7) ─────────────────────────────────────────────────
function innerAD(sessionId, transcriptHash) {
    return concatBytes(INNER_AD_PREFIX, sessionId, transcriptHash);
}
/**
 * Per-fragment associated data (§7). Binds classification, position, total
 * count, direction, session, and transcript. Never transmitted on the wire.
 */
export function fragmentAD(kind, index, totalCount, direction, sessionId, transcriptHash) {
    return concatBytes(FRAGMENT_AD_PREFIX, Uint8Array.of(kind), uint32be(index), uint32be(totalCount), Uint8Array.of(directionByte(direction)), sessionId, transcriptHash);
}
// ── Fragment construction ────────────────────────────────────────────────────
function randomNonce(index, deterministicNonce) {
    if (deterministicNonce) {
        const n = deterministicNonce(index);
        requireLength(n, NONCE_SIZE, 'deterministicNonce(index)');
        return n;
    }
    return sodium.randombytes_buf(NONCE_SIZE);
}
/**
 * AEAD-seal one FRAGMENT_INNER_SIZE-byte chunk and split the S0 box (which is
 * `ciphertext || tag`) into the fixed-width wire fields.
 */
function sealFragment(chunk, key, ad, nonce) {
    const box = xchacha20poly1305Encrypt(chunk, ad, key, nonce);
    // box.ciphertext = chunk-ciphertext (FRAGMENT_INNER_SIZE) || tag (TAG_SIZE).
    return {
        nonce: box.nonce,
        ciphertext: box.ciphertext.slice(0, FRAGMENT_INNER_SIZE),
        tag: box.ciphertext.slice(FRAGMENT_INNER_SIZE),
    };
}
// ── Public API (§11.2) ───────────────────────────────────────────────────────
/**
 * Encrypt `payload` under `contentKey`, pad, fragment, and optionally generate
 * chaff so the total emitted fragment count equals `opts.fixedTotalCount`.
 */
export function encryptAndFragment(payload, contentKey, transcriptHash, sessionId, direction, opts = {}) {
    if (!(payload instanceof Uint8Array)) {
        throw new FumeCryptoError('payload must be a Uint8Array');
    }
    requireLength(contentKey, CONTENT_KEY_SIZE, 'contentKey');
    requireLength(transcriptHash, TRANSCRIPT_HASH_SIZE, 'transcriptHash');
    requireLength(sessionId, SESSION_ID_SIZE, 'sessionId');
    directionByte(direction); // validates
    const { K_frag_real } = deriveFragmentKeys(contentKey, transcriptHash);
    // Inner encrypt: padded payload → inner ciphertext (exact multiple of 1024).
    const padded = padPayload(payload);
    const innerBox = xchacha20poly1305Encrypt(padded, innerAD(sessionId, transcriptHash), contentKey, INNER_NONCE);
    const innerCiphertext = innerBox.ciphertext;
    if (innerCiphertext.length % FRAGMENT_INNER_SIZE !== 0) {
        // Unreachable given the padding scheme; defensive.
        throw new FumeCryptoError('inner ciphertext is not a whole number of fragments');
    }
    const realCount = innerCiphertext.length / FRAGMENT_INNER_SIZE;
    let totalCount = realCount;
    let chaffCount = 0;
    if (opts.fixedTotalCount !== undefined) {
        const f = opts.fixedTotalCount;
        if (!Number.isInteger(f) || f < 0) {
            throw new FumeCryptoError('fixedTotalCount must be a non-negative integer');
        }
        if (f < realCount) {
            throw new FumeCryptoError(`fixedTotalCount (${f}) is smaller than the required real fragment count (${realCount})`);
        }
        totalCount = f;
        chaffCount = f - realCount;
    }
    const realFragments = [];
    for (let i = 0; i < realCount; i++) {
        const chunk = innerCiphertext.subarray(i * FRAGMENT_INNER_SIZE, (i + 1) * FRAGMENT_INNER_SIZE);
        const ad = fragmentAD(KIND_REAL, i, totalCount, direction, sessionId, transcriptHash);
        const sealed = sealFragment(chunk, K_frag_real, ad, randomNonce(i, opts.deterministicNonce));
        realFragments.push({ kind: 'real', index: i, totalCount, direction, ...sealed });
    }
    const chaffFragments = chaffCount > 0
        ? generateChaff(chaffCount, contentKey, transcriptHash, sessionId, direction, totalCount, opts.deterministicNonce ? { deterministicNonce: opts.deterministicNonce } : undefined)
        : [];
    return { realFragments, chaffFragments, totalCount, realCount };
}
/**
 * Generate `count` standalone chaff fragments occupying the tail index range
 * `[totalCount - count, totalCount - 1]` (so they slot in after `realCount`
 * real fragments; §9). Each seals FRAGMENT_INNER_SIZE random bytes under the
 * chaff subkey — byte-length- and shape-identical to a real fragment (§8).
 */
export function generateChaff(count, contentKey, transcriptHash, sessionId, direction, totalCount, opts) {
    if (!Number.isInteger(count) || count < 0) {
        throw new FumeCryptoError('count must be a non-negative integer');
    }
    if (!Number.isInteger(totalCount) || totalCount < count) {
        throw new FumeCryptoError('totalCount must be an integer >= count');
    }
    requireLength(contentKey, CONTENT_KEY_SIZE, 'contentKey');
    requireLength(transcriptHash, TRANSCRIPT_HASH_SIZE, 'transcriptHash');
    requireLength(sessionId, SESSION_ID_SIZE, 'sessionId');
    directionByte(direction); // validates
    const { K_frag_chaff } = deriveFragmentKeys(contentKey, transcriptHash);
    const startIndex = totalCount - count;
    const out = [];
    for (let j = 0; j < count; j++) {
        const index = startIndex + j;
        const inner = sodium.randombytes_buf(FRAGMENT_INNER_SIZE);
        const ad = fragmentAD(KIND_CHAFF, index, totalCount, direction, sessionId, transcriptHash);
        const sealed = sealFragment(inner, K_frag_chaff, ad, randomNonce(index, opts?.deterministicNonce));
        out.push({ kind: 'chaff', index, totalCount, direction, ...sealed });
    }
    return out;
}
/**
 * Serialize a fragment to its fixed FRAGMENT_SIZE (1064) byte wire form:
 * `nonce (24) || ciphertext (1024) || tag (16)`. Classification and metadata
 * are NOT serialized (they are carried out-of-band by the transport layer).
 */
export function serializeFragment(fragment) {
    requireLength(fragment.nonce, NONCE_SIZE, 'fragment.nonce');
    requireLength(fragment.ciphertext, FRAGMENT_INNER_SIZE, 'fragment.ciphertext');
    requireLength(fragment.tag, TAG_SIZE, 'fragment.tag');
    const out = new Uint8Array(FRAGMENT_SIZE);
    out.set(fragment.nonce, 0);
    out.set(fragment.ciphertext, NONCE_SIZE);
    out.set(fragment.tag, NONCE_SIZE + FRAGMENT_INNER_SIZE);
    return out;
}
/**
 * Parse the fixed FRAGMENT_SIZE wire form back into its byte fields. The wire
 * form carries no classification/metadata (those are bound only in the AD and
 * supplied out-of-band by the transport), so `meta` may be provided to attach
 * the known {kind, index, totalCount, direction}; it defaults to a real
 * fragment at index 0. Verification does not depend on these defaults — the AD
 * that authenticates a fragment is reconstructed from the caller-supplied
 * metadata at reassembly time.
 */
export function parseFragment(bytes, meta) {
    requireLength(bytes, FRAGMENT_SIZE, 'fragment bytes');
    return {
        kind: meta?.kind ?? 'real',
        index: meta?.index ?? 0,
        totalCount: meta?.totalCount ?? 0,
        direction: meta?.direction ?? 'send',
        nonce: bytes.slice(0, NONCE_SIZE),
        ciphertext: bytes.slice(NONCE_SIZE, NONCE_SIZE + FRAGMENT_INNER_SIZE),
        tag: bytes.slice(NONCE_SIZE + FRAGMENT_INNER_SIZE, FRAGMENT_SIZE),
    };
}
/**
 * Recombine the S0 wire box (`ciphertext || tag`) from a fragment's split
 * fields and authenticate-decrypt it under `key` with the reconstructed AD.
 * Returns the recovered chunk, or `null` on any authentication failure.
 */
function openFragment(fragment, key, ad) {
    if (!(fragment.nonce instanceof Uint8Array) ||
        fragment.nonce.length !== NONCE_SIZE ||
        !(fragment.ciphertext instanceof Uint8Array) ||
        fragment.ciphertext.length !== FRAGMENT_INNER_SIZE ||
        !(fragment.tag instanceof Uint8Array) ||
        fragment.tag.length !== TAG_SIZE) {
        return null;
    }
    const box = concatBytes(fragment.ciphertext, fragment.tag);
    try {
        return xchacha20poly1305Decrypt(box, ad, key, fragment.nonce);
    }
    catch {
        return null;
    }
}
/**
 * Verify every fragment, drop authenticated chaff, order real fragments by
 * index, inner-decrypt, strip padding, and return the exact payload.
 *
 * Throws {@link FumeCryptoError} on any authentication failure, wrong
 * T/session/direction, missing/duplicate/non-contiguous real index, empty real
 * set, or invalid padding (invariant I3 / gate G5).
 */
export function verifyAndReassemble(fragments, contentKey, transcriptHash, sessionId, direction, opts = {}) {
    if (!Array.isArray(fragments)) {
        throw new FumeCryptoError('fragments must be an array');
    }
    requireLength(contentKey, CONTENT_KEY_SIZE, 'contentKey');
    requireLength(transcriptHash, TRANSCRIPT_HASH_SIZE, 'transcriptHash');
    requireLength(sessionId, SESSION_ID_SIZE, 'sessionId');
    directionByte(direction); // validates
    if (opts.fixedTotalCount !== undefined && fragments.length !== opts.fixedTotalCount) {
        throw new FumeCryptoError('fragment count does not match the expected fixedTotalCount');
    }
    const { K_frag_real, K_frag_chaff } = deriveFragmentKeys(contentKey, transcriptHash);
    const realChunks = new Map();
    for (const fragment of fragments) {
        const realAD = fragmentAD(KIND_REAL, fragment.index, fragment.totalCount, direction, sessionId, transcriptHash);
        const chunk = openFragment(fragment, K_frag_real, realAD);
        if (chunk !== null) {
            if (realChunks.has(fragment.index)) {
                throw new FumeCryptoError('duplicate real fragment index');
            }
            realChunks.set(fragment.index, chunk);
            continue;
        }
        // Not a real fragment; must authenticate as chaff or the set is corrupt.
        const chaffAD = fragmentAD(KIND_CHAFF, fragment.index, fragment.totalCount, direction, sessionId, transcriptHash);
        if (openFragment(fragment, K_frag_chaff, chaffAD) === null) {
            throw new FumeCryptoError('fragment failed authentication (wrong key/transcript/session/direction or tampered)');
        }
        // Authenticated chaff: drop it.
    }
    const realCount = realChunks.size;
    if (realCount === 0) {
        throw new FumeCryptoError('no authenticated real fragments to reassemble');
    }
    // Real indices must be exactly the contiguous set {0, 1, ..., realCount-1}.
    const ordered = new Uint8Array(realCount * FRAGMENT_INNER_SIZE);
    for (let i = 0; i < realCount; i++) {
        const chunk = realChunks.get(i);
        if (chunk === undefined) {
            throw new FumeCryptoError('missing or non-contiguous real fragment index');
        }
        ordered.set(chunk, i * FRAGMENT_INNER_SIZE);
    }
    // Inner decrypt then strip padding. Either step rejects on tamper.
    const padded = xchacha20poly1305Decrypt(ordered, innerAD(sessionId, transcriptHash), contentKey, INNER_NONCE);
    return stripPadding(padded);
}
//# sourceMappingURL=fragment.js.map